// src/pages/UnifiedPlayer/CommentsSection.tsx
// Extracted verbatim from UnifiedPlayerPage.tsx as part of splitting that
// ~3000-line file into smaller, independently-verifiable pieces. This is a
// self-contained slice: all comment state/handlers/rendering lived only
// here in the original file (nothing outside the comments block referenced
// them), so behavior, prop flow, and effect timing are unchanged - this is
// a structural move, not a rewrite.
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MessageCircle, Send, User, ThumbsUp, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import type { RootState } from '../../store';
import { uiActions } from '../../store/reducers/uiReducers';
import commentService, { type Comment } from '../../services/commentService';
import { getFullAvatarUrl } from './playerHelpers';
import type { UnifiedContent } from './UnifiedPlayerPage';

interface CommentsSectionProps {
  content: UnifiedContent | null;
  requireAuth: (action: string) => boolean;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({ content, requireAuth }) => {
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const dispatch = useDispatch();

  const commentsLoadedRef = useRef<string | null>(null);

  // UI state
  const [showComments, setShowComments] = useState(true);

  // Comments state
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);

  // Edit comment state
  const [editingComment, setEditingComment] = useState<{ id: string; text: string } | null>(null);
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ commentId: string; isVisible: boolean }>({ commentId: '', isVisible: false });

  // Fetch comments
  const fetchComments = useCallback(async (forceRefresh = false) => {
    if (!content || !content.id) {
      // Cannot fetch comments: missing content or content ID
      return;
    }

    const cacheKey = `${content.type}-${content.id}`;
    if (!forceRefresh && commentsLoadedRef.current === cacheKey) return;

    try {
      setCommentsLoading(true);

      let commentsData;

      // Use the unified getContentComments method for most content types
      if (['cinema', 'content', 'animation', 'sneak-peek', 'live-video', 'podcast'].includes(content.type)) {
        commentsData = await commentService.getContentComments(
          content.id,
          content.type as 'cinema' | 'content' | 'podcast' | 'animation' | 'sneak-peek' | 'live-video'
        );
      } else {
        // Fallback to generic comment fetching (for any other content types)
        commentsData = await commentService.getComments({
          content_type: content.type,
          object_id: content.id
        });
      }

      const comments = commentsData.results || commentsData || [];
      setComments(comments);
      commentsLoadedRef.current = cacheKey;
    } catch (err) {
      console.error('Error fetching comments:', err);
      setComments([]); // Set empty array on error
    } finally {
      setCommentsLoading(false);
    }
  }, [content]);

  useEffect(() => {
    if (content) {
      fetchComments();
    }
  }, [content, fetchComments]);

  // Comment handlers
  const handleSubmitComment = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content || !newComment.trim()) return;
    if (!requireAuth('post a comment')) return;

    try {
      setIsSubmittingComment(true);
      await commentService.createComment({
        contentType: content.type,
        contentId: content.id,
        text: newComment.trim(),
        parentId: replyTo || undefined
      });

      // Force refetch comments to get the full nested structure from backend
      await fetchComments(true);

      setNewComment('');
      setReplyTo(null);

      dispatch(uiActions.addNotification({
        message: 'Comment posted successfully!',
        type: 'success'
      }));
    } catch (err) {
      console.error('Error posting comment:', err);
      dispatch(uiActions.addNotification({
        message: 'Failed to post comment. Please try again.',
        type: 'error'
      }));
    } finally {
      setIsSubmittingComment(false);
    }
  }, [content, requireAuth, newComment, replyTo, dispatch, fetchComments]);

  // Helper function to get user interaction (handles both snake_case and camelCase)
  const getUserInteraction = useCallback((comment: Comment) => {
    return comment.user_interaction || comment.userInteraction || { liked: false, disliked: false };
  }, []);

  // Comment action handlers
  const handleLikeComment = useCallback(async (commentId: string) => {
    if (!requireAuth('like comments')) return;

    // Helper to recursively find comment
    const findComment = (comments: Comment[], id: string): Comment | null => {
      for (const comment of comments) {
        if (comment.id === id) return comment;
        if (comment.replies) {
          const found = findComment(comment.replies, id);
          if (found) return found;
        }
      }
      return null;
    };

    const targetComment = findComment(comments, commentId);
    const isCurrentlyLiked = targetComment ? getUserInteraction(targetComment).liked : false;

    // Optimistic UI update - recursively update comment
    const updateCommentLike = (comments: Comment[]): Comment[] => {
      return comments.map(comment => {
        if (comment.id === commentId) {
          const newLikeCount = isCurrentlyLiked
            ? Math.max(0, ((comment.likeCount || comment.like_count) || 0) - 1)
            : ((comment.likeCount || comment.like_count) || 0) + 1;

          return {
            ...comment,
            likeCount: newLikeCount,
            like_count: newLikeCount,
            user_interaction: {
              liked: !isCurrentlyLiked,
              disliked: getUserInteraction(comment).disliked
            },
            userInteraction: {
              liked: !isCurrentlyLiked,
              disliked: getUserInteraction(comment).disliked
            }
          };
        }
        if (comment.replies && comment.replies.length > 0) {
          return { ...comment, replies: updateCommentLike(comment.replies) };
        }
        return comment;
      });
    };

    setComments(prev => updateCommentLike(prev));

    try {
      await commentService.likeComment(commentId);

      dispatch(uiActions.addNotification({
        message: isCurrentlyLiked ? 'Comment unliked!' : 'Comment liked!',
        type: 'success'
      }));

      // Optional: Fetch updated data from backend to ensure consistency
      // This will correct any discrepancies but won't cause UI flicker
      setTimeout(() => {
        fetchComments();
      }, 1000); // Delay to avoid immediate UI changes

    } catch (err) {
      console.error('Error toggling comment like:', err);
      dispatch(uiActions.addNotification({
        message: 'Failed to toggle comment like.',
        type: 'error'
      }));
    }
  }, [requireAuth, dispatch, comments, getUserInteraction, fetchComments]);

  const handleEditComment = useCallback(async (comment: Comment) => {
    setEditingComment({ id: comment.id, text: comment.text });
  }, []);

  const handleUpdateComment = useCallback(async (commentId: string, newText: string) => {
    if (!userInfo || !newText.trim()) return;

    try {
      setIsUpdatingComment(true);
      await commentService.updateComment(commentId, newText.trim());

      // Update comment in state - recursively
      const updateCommentText = (comments: Comment[]): Comment[] => {
        return comments.map(comment => {
          if (comment.id === commentId) {
            return { ...comment, text: newText.trim(), is_edited: true };
          }
          if (comment.replies && comment.replies.length > 0) {
            return { ...comment, replies: updateCommentText(comment.replies) };
          }
          return comment;
        });
      };

      setComments(prev => updateCommentText(prev));

      setEditingComment(null);
      dispatch(uiActions.addNotification({
        message: 'Comment updated successfully.',
        type: 'success'
      }));
    } catch (err) {
      console.error('Error updating comment:', err);
      dispatch(uiActions.addNotification({
        message: 'Failed to update comment.',
        type: 'error'
      }));
    } finally {
      setIsUpdatingComment(false);
    }
  }, [userInfo, dispatch]);

  const handleCancelEdit = useCallback(() => {
    setEditingComment(null);
  }, []);

  const handleDeleteComment = useCallback(async (commentId: string) => {
    if (!userInfo) return;
    setDeleteConfirmation({ commentId, isVisible: true });
  }, [userInfo]);

  const confirmDeleteComment = useCallback(async () => {
    const { commentId } = deleteConfirmation;
    if (!commentId) return;

    try {
      await commentService.deleteComment(commentId);

      // Remove comment from state - recursively
      const deleteCommentRecursive = (comments: Comment[]): Comment[] => {
        return comments
          .filter(comment => comment.id !== commentId)
          .map(comment => {
            if (comment.replies && comment.replies.length > 0) {
              return { ...comment, replies: deleteCommentRecursive(comment.replies) };
            }
            return comment;
          });
      };

      setComments(prev => deleteCommentRecursive(prev));

      setDeleteConfirmation({ commentId: '', isVisible: false });
      dispatch(uiActions.addNotification({
        message: 'Comment deleted successfully.',
        type: 'success'
      }));
    } catch (err) {
      console.error('Error deleting comment:', err);
      dispatch(uiActions.addNotification({
        message: 'Failed to delete comment.',
        type: 'error'
      }));
    }
  }, [deleteConfirmation, dispatch]);

  const cancelDeleteComment = useCallback(() => {
    setDeleteConfirmation({ commentId: '', isVisible: false });
  }, []);

  const renderCommentWithReplies = (comment: Comment, depth: number): JSX.Element => {
    const avatarSize = depth === 0 ? 'w-8 h-8' : 'w-6 h-6';
    const textSize = depth === 0 ? 'text-sm' : 'text-xs';
    const nameSize = depth === 0 ? 'text-sm' : 'text-xs';
    const indentClass = depth > 0 ? 'mt-4 pl-4 border-l border-gray-800' : '';

    return (
      <div key={comment.id} className={`${depth === 0 ? 'border-l-2 border-gray-700 pl-4' : indentClass}`}>
        {/* Comment Header */}
        <div className="flex items-start space-x-3">
          <div className={`${avatarSize} rounded-full bg-gradient-to-br from-orange-500 to-blue-600 flex items-center justify-center text-white ${depth === 0 ? 'text-sm' : 'text-xs'} font-semibold flex-shrink-0`}>
            {(() => {
              const avatarUrl = getFullAvatarUrl(comment.user?.avatar_url || comment.user?.avatarUrl);
              if (avatarUrl) {
                return (
                  <img
                    src={avatarUrl}
                    alt={`${comment.user?.first_name || comment.user?.firstName || 'User'} ${comment.user?.last_name || comment.user?.lastName || ''}`}
                    className="w-full h-full rounded-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                );
              }
              const firstName = comment.user?.first_name || comment.user?.firstName || 'U';
              const lastName = comment.user?.last_name || comment.user?.lastName || '';
              return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
            })()}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className={`font-medium text-white ${nameSize}`}>
                {comment.user?.first_name || comment.user?.firstName || 'Anonymous'} {comment.user?.last_name || comment.user?.lastName || ''}
              </span>
              <span className="text-xs text-gray-500">
                {(() => {
                  const dateStr = comment.created_at || comment.createdAt;
                  if (!dateStr) return 'Invalid date';
                  try {
                    return new Date(dateStr).toLocaleDateString();
                  } catch {
                    return 'Invalid date';
                  }
                })()}
              </span>
              {comment.is_edited && (
                <span className="text-xs text-gray-500">(edited)</span>
              )}
            </div>
            {editingComment?.id === comment.id ? (
              <div className="mb-2">
                <textarea
                  value={editingComment.text}
                  onChange={(e) => setEditingComment({ ...editingComment, text: e.target.value })}
                  className={`w-full bg-gray-800 text-white rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 ${textSize}`}
                  rows={2}
                />
                <div className="flex items-center space-x-2 mt-2">
                  <button
                    onClick={() => handleUpdateComment(comment.id, editingComment.text)}
                    disabled={!editingComment.text.trim() || isUpdatingComment}
                    className={`px-${depth === 0 ? '3' : '2'} py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isUpdatingComment ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className={`px-${depth === 0 ? '3' : '2'} py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className={`text-gray-300 ${textSize} mb-2`}>{comment.text}</p>
            )}

            {/* Comment Actions */}
            <div className="flex items-center space-x-4 text-xs">
              <button
                onClick={() => handleLikeComment(comment.id)}
                className={`flex items-center space-x-1 transition-colors ${
                  getUserInteraction(comment).liked
                    ? 'text-orange-400'
                    : 'text-gray-500 hover:text-orange-400'
                }`}
              >
                <ThumbsUp className={`w-3 h-3 ${
                  getUserInteraction(comment).liked ? 'fill-current' : ''
                }`} />
                <span>{comment.likeCount || comment.like_count || 0}</span>
              </button>

              <button
                onClick={() => setReplyTo(comment.id)}
                className="text-gray-500 hover:text-white"
              >
                Reply
              </button>

              {(userInfo?.id === comment.user?.id || userInfo?.isAdmin) && (
                <>
                  <button
                    onClick={() => handleEditComment(comment)}
                    className="text-gray-500 hover:text-white"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    className="text-gray-500 hover:text-red-400"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>

            {/* Recursively render nested replies */}
            {comment.replies && comment.replies.length > 0 && (
              <div className="space-y-3">
                {comment.replies.map((reply) => renderCommentWithReplies(reply, depth + 1))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Comments Section */}
      <div className="bg-gray-900 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <MessageCircle className="w-5 h-5 mr-2" />
            Comments ({comments.length})
          </h3>
          <button
            onClick={() => setShowComments(!showComments)}
            className="text-gray-400 hover:text-white"
          >
            {showComments ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>

        {showComments && (
          <>
            {/* Comment Form */}
            <form onSubmit={handleSubmitComment} className="mb-6">
              <div className="flex space-x-3">
                {userInfo?.avatar ? (
                  <img
                    src={getFullAvatarUrl(userInfo.avatar)}
                    alt={userInfo.firstName || userInfo.email}
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={replyTo ? "Write a reply..." : "Add a comment..."}
                    className="w-full bg-gray-800 text-white rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                    rows={3}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-2">
                      {replyTo && (
                        <button
                          type="button"
                          onClick={() => setReplyTo(null)}
                          className="text-sm text-gray-400 hover:text-white"
                        >
                          Cancel Reply
                        </button>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={!newComment.trim() || isSubmittingComment}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isSubmittingComment ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      <span>{replyTo ? 'Reply' : 'Comment'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </form>

            {/* Comments List */}
            <div className="space-y-4">
              {commentsLoading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-gray-400">Loading comments...</p>
                </div>
              ) : comments.length > 0 ? (
                comments.map((comment) => renderCommentWithReplies(comment, 0))
              ) : (
                <div className="text-center py-8">
                  <MessageCircle className="h-12 w-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-400">No comments yet. Be the first to comment!</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      {deleteConfirmation.isVisible && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-800/95 backdrop-blur-md rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Delete Comment</h3>
            <p className="text-gray-300 mb-6">Are you sure you want to delete this comment? This action cannot be undone.</p>
            <div className="flex items-center space-x-4">
              <button
                onClick={confirmDeleteComment}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={cancelDeleteComment}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommentsSection;
